import { Link } from 'react-router-dom';
import { useLanguage } from './i18n/LanguageContext';

function Business() {
  const { t } = useLanguage();
  const businessLines = [
    {
      title: t.business.bts.title,
      description: t.business.bts.description,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2 22h20M2 22V8l10-6 10 6v14M6 22V12h4v10M14 22V12h4v10M10 8h4" />
        </svg>
      )
    },
    {
      title: t.business.readyBuilt.title,
      description: t.business.readyBuilt.description,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    },
    {
      title: t.business.assetMgmt.title,
      description: t.business.assetMgmt.description,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      title: t.business.fundMgmt.title,
      description: t.business.fundMgmt.description,
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  return (
    <div className="page-content">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img src="/office-work.jpg" alt="Our Business" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-dark/70 via-navy-primary/60 to-blue-accent/30"></div>

        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-accent/20 rounded-full blur-3xl animate-pulse"></div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-8 animate-fade-in-up">
            <span className="inline-block px-4 py-2 mb-6 text-sm font-medium tracking-widest uppercase bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
              {t.business.heroTag}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-4 tracking-tight">
              {t.business.heroTitle}
            </h1>
            <p className="text-xl font-light opacity-90 max-w-2xl mx-auto">
              {t.business.heroSubtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Business Lines Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="space-y-24">
            {businessLines.map((business, idx) => (
              <div
                key={idx}
                className={`flex flex-col ${idx % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center`}
              >
                {/* Content */}
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-accent/10 to-gold-accent/10 flex items-center justify-center text-blue-accent">
                      {business.icon}
                    </div>
                    <span className="text-5xl font-light text-gray-200">0{idx + 1}</span>
                  </div>
                  <h2 className="text-3xl font-light text-navy-primary mb-6">
                    {business.title}
                  </h2>
                  <p className="text-gray-600 leading-relaxed text-lg">
                    {business.description}
                  </p>
                </div>

                {/* Visual */}
                <div className="lg:w-1/2">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-accent/20 to-gold-accent/20 rounded-2xl transform rotate-3 group-hover:rotate-6 transition-transform duration-500"></div>
                    <div className="relative bg-gradient-to-br from-navy-primary to-navy-light rounded-2xl p-12 text-white">
                      <div className="text-center">
                        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
                          {business.icon}
                        </div>
                        <h3 className="text-xl font-medium mb-2">{business.title}</h3>
                        <div className="w-12 h-0.5 bg-gold-accent mx-auto"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-dark via-navy-primary to-navy-light text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-accent rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-gold-accent rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto px-8 text-center relative">
          <h2 className="text-3xl md:text-4xl font-light text-white mb-4">{t.business.ctaTitle}</h2>
          <p className="text-white/70 text-lg mb-8">
            {t.business.ctaSubtitle}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-accent to-light-blue text-white font-medium rounded-lg hover:shadow-2xl hover:shadow-blue-accent/30 transition-all duration-300 hover:-translate-y-1"
          >
            {t.home.getInTouch}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Business;
